// Client layer (TS-2.5.03). "Show area" is single-select (RoadmapFilterSemantics.areaSelection
// === "SingleSelect") and hides both swimlane rows and detail sections; "Filter status" is
// multi-select additive (statusSelection === "MultiSelectAdditive") and hides detail cards while
// only dimming board chips - decomposition.md section 8.7 point 9 / decisions.md D-199. Every
// toggle carries aria-pressed (closing the WCAG AA gap point 11 flags in the reference output,
// whose `.fbtn`/`.gbtn` toggles carried none) and every selection is echoed back to the caller as
// the FilterChoice CODE the API round-trips, never the display label (D-199's whole point).
// Owner feedback (same batch as D-282): each row now carries a VISIBLE leading title - the same
// "Show area"/"Filter status" strings that were previously aria-label-only - associated via
// aria-labelledby (useId), so the visible and accessible names cannot drift apart. The inline
// leading-label placement matches the roadmap's own "Group by:" convention (GroupModeToggle).
import { useId } from "react";
import type { FilterChoice } from "../../api/generated/contracts";
import { strings } from "../../strings";

export interface FilterBarProps {
  readonly areas: readonly FilterChoice[];
  readonly statuses: readonly FilterChoice[];
  readonly selectedArea: string | null;
  readonly selectedStatuses: readonly string[];
  readonly onAreaChange: (code: string | null) => void;
  readonly onStatusesChange: (codes: readonly string[]) => void;
}

export function FilterBar({ areas, statuses, selectedArea, selectedStatuses, onAreaChange, onStatusesChange }: FilterBarProps) {
  const areaLabelId = useId();
  const statusLabelId = useId();

  function toggleArea(code: string) {
    onAreaChange(selectedArea === code ? null : code);
  }

  function toggleStatus(code: string) {
    onStatusesChange(
      selectedStatuses.includes(code) ? selectedStatuses.filter((selected) => selected !== code) : [...selectedStatuses, code],
    );
  }

  return (
    <div className="eg-filter-bar">
      <div role="group" aria-labelledby={areaLabelId} className="eg-filter-group">
        <span id={areaLabelId} className="eg-filter-group__label">
          {strings.roadmap.filters.showArea}
        </span>
        {/* The options wrapper keeps wrapped chip lines inside the control column of the shared
            label-column grid (roadmap.css) instead of sliding under the label. */}
        <div className="eg-filter-group__options">
          {areas.map((choice) => (
            <button
              key={choice.code}
              type="button"
              aria-pressed={selectedArea === choice.code}
              className="eg-filter-toggle"
              onClick={() => toggleArea(choice.code)}
            >
              {choice.label} ({choice.matchCount})
            </button>
          ))}
        </div>
      </div>
      <div role="group" aria-labelledby={statusLabelId} className="eg-filter-group">
        <span id={statusLabelId} className="eg-filter-group__label">
          {strings.roadmap.filters.filterStatus}
        </span>
        <div className="eg-filter-group__options">
          {statuses.map((choice) => (
            <button
              key={choice.code}
              type="button"
              aria-pressed={selectedStatuses.includes(choice.code)}
              className="eg-filter-toggle"
              onClick={() => toggleStatus(choice.code)}
            >
              {choice.label} ({choice.matchCount})
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
